import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { FullscreenModal } from "./fullscreen-modal";

interface PdfPreviewProps {
  fileUrl: string;
  fileName: string;
  hidePreviewButton?: boolean;
}

export const PdfPreview: React.FC<PdfPreviewProps> = ({
  fileUrl,
  fileName,
  hidePreviewButton = false,
}) => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handlePreviewClick = () => {
    setShowPreview(!showPreview);
  };

  const handleFullscreenClick = () => {
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <Icon icon="mdi:file-pdf-box" className="text-4xl text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-medium text-gray-900 truncate"
            title={fileName}
          >
            {fileName}
          </p>
          <p className="text-xs text-gray-500">{t("forms.document")}</p>
        </div>
        <div className="flex space-x-2">
          {!hidePreviewButton && (
            <button
              type="button"
              onClick={handlePreviewClick}
              className="flex-shrink-0 px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
            >
              {showPreview ? t("forms.hidePreview") : t("forms.preview")}
            </button>
          )}
          <button
            type="button"
            onClick={handleFullscreenClick}
            className="flex-shrink-0 px-3 py-1 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200 transition-colors"
            title={t("forms.openInFullscreen")}
          >
            <Icon icon="mdi:fullscreen" className="text-sm" />
          </button>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
          >
            {t("forms.viewPdf")}
          </a>
        </div>
      </div>

      {showPreview && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">
                {t("forms.pdfPreview")}
              </h4>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Icon icon="mdi:information" className="text-sm" />
                <span>{t("forms.interactivePreview")}</span>
              </div>
            </div>

            <div className="bg-gray-50 rounded border border-gray-200 overflow-hidden">
              <div className="w-full h-[600px] overflow-auto">
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    transform: "scale(0.2)",
                    transformOrigin: "top left",
                    overflow: "auto",
                  }}
                >
                  <iframe
                    src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                    width="500%"
                    height="500%"
                    frameBorder="0"
                    className="rounded"
                    style={{
                      border: "none",
                    }}
                    title={`Preview of ${fileName}`}
                    onError={(e) => {
                      const target = e.target as HTMLIFrameElement;
                      target.style.display = "none";
                      const parent = target.parentElement?.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                      <div class="text-center py-8">
                        <div class="text-gray-400 mb-2">
                          <svg class="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"></path>
                          </svg>
                        </div>
                        <p class="text-sm text-gray-500">Preview not available</p>
                        <p class="text-xs text-gray-400 mt-1">Click "View PDF" to open the document</p>
                      </div>
                    `;
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Modal */}
      <FullscreenModal
        isOpen={isFullscreen}
        onClose={handleCloseFullscreen}
        title={`PDF Document - ${fileName}`}
      >
        <div className="h-full">
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            className="rounded"
            title={`Fullscreen preview of ${fileName}`}
            onError={(e) => {
              // Fallback if preview fails
              const target = e.target as HTMLIFrameElement;
              target.style.display = "none";
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div class="flex items-center justify-center h-full">
                    <div class="text-center py-8">
                      <div class="text-gray-400 mb-4">
                        <svg class="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"></path>
                        </svg>
                      </div>
                      <p class="text-lg text-gray-500 mb-2">Preview not available</p>
                      <p class="text-sm text-gray-400 mb-4">Click "View PDF" to open the document</p>
                      <a href="${fileUrl}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
                        <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                        </svg>
                        View PDF
                      </a>
                    </div>
                  </div>
                `;
              }
            }}
          />
        </div>
      </FullscreenModal>
    </div>
  );
};
